import React, { useEffect } from 'react';
import { useHistory } from "react-router-dom";
import { makeStyles } from '@material-ui/core/styles';

import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import Icon from '@material-ui/core/Icon';
import IconButton from '@material-ui/core/IconButton';

import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';

import VenueBalancesFilter from './VenueBalancesFilter';
import ReportToolbar from './ReportToolbar';
import ToggleShowEmail from './ToggleShowEmail';


const useStyles = makeStyles(theme => ({
    paper: {
        flexGrow: 1,
        backgroundColor: theme.palette.background.paper,
        padding: theme.spacing(3),
    },
    table: {
        minWidth: 650,
    },
    venueCol: {
        maxWidth: 180,
    },
    coupleCol: {
        minWidth: 150,
    },
    notesCol: {
        maxWidth: 300,
        minWidth: 150,
    },
    tableHeader: {
        backgroundColor: theme.palette.primary.main,
        '& .MuiTableCell-head': {
            color: theme.palette.primary.contrastText,
        },
    },
}));

const VenueBalancesReport = () => {
    const classes = useStyles();
    const reportUrl = '/api/reports/venue-balances';
    const reportTitle = 'Venues and Balance';
    const pageTitle = `${reportTitle} - Enduring Promises`;
    const [data, setData] = React.useState(null);
    const [loading, setLoading] = React.useState(true);

    const [state, setState] = React.useState({
        year: new Date().getFullYear(),
        venue: 'Guild',
    });

    const getData = state => {
        new Promise((resolve, reject) => {
            fetch(`${reportUrl}?year=${state.year}&search=${state.venue}`)
                .then(response => {
                    return response.json();
                })
                .then(result => {
                    setData(result);
                    resolve();
                })
                .finally(() => {
                    setLoading(false);
                });
        });
    };

    useEffect(() => {
        document.title = pageTitle;
        getData(state);
    }, [1]);

    const handleSubmit = () => {
        getData(state);
    };

    const [showEmails, setShowEmails] = React.useState(true);

    let history = useHistory();
    const handleClick = id => event => {
        history.push(`/admin/couples/${id}`);
    }

    const csvData = (data) => {
        const rows = [];
        Object.values(data).forEach(row => {
            rows.push([
                row.wedding.wedding_date_formatted,
                row.wedding.bride_full_name,
                row.wedding.groom_full_name,
                row.wedding.bride_email,
                row.wedding.groom_email,
                row.wedding.officiant ? row.wedding.officiant.name : '',
                row.wedding.status,
                row.wedding.balance,
                row.wedding.invoices,
                row.wedding.venue_name,
                row.wedding.all_notes,
                row.wedding.location_rehearsal,
                row.wedding.requires_rehearsal ? "Yes" : "No",
            ]);
        });
        return {
            filename: 'venues-balances.csv',
            columns: [
                'Wedding Date', 'Bride Name', 'Groom Name', 'Bride Email',
                'Groom Email', 'Officiant', 'Status', 'Balance', '# Invoices',
                'Venue', 'Notes', 'Rehearsal Location', 'Requires Rehearsal'
            ],
            rows,
        }
    }

    return (
        <Paper square className={classes.paper}>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Typography variant="h6">{reportTitle}</Typography>
                </Grid>
                <Grid item xs={8}>
                    <VenueBalancesFilter state={state} setState={setState} handleSubmit={handleSubmit} />
                    <ToggleShowEmail showEmails={showEmails} setShowEmails={setShowEmails} />
                </Grid>
                <Grid item xs={4}>
                    <Grid
                        container
                        direction="row"
                        justify="flex-end"
                        alignItems="center"
                    >
                        {data !== null && <ReportToolbar data={csvData(data)} />}
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <TableContainer component={Paper} variant="outlined" square>
                        <Table className={classes.table} size="small">
                            <TableHead className={classes.tableHeader}>
                                <TableRow>
                                    <TableCell align="left">Wedding Date</TableCell>
                                    <TableCell align="left">Couple</TableCell>
                                    {showEmails && (
                                        <React.Fragment>
                                            <TableCell align="left">Bride Email</TableCell>
                                            <TableCell align="left">Groom Email</TableCell>
                                        </React.Fragment>
                                    )}
                                    <TableCell align="left">Officiant</TableCell>
                                    <TableCell align="left">Status</TableCell>
                                    <TableCell align="left">Balance</TableCell>
                                    <TableCell align="left">#Invoices</TableCell>
                                    <TableCell align="left">Venue</TableCell>
                                    <TableCell align="left">Notes</TableCell>
                                    <TableCell align="left">Rehearsal Location</TableCell>
                                    <TableCell align="left">Requires Rehearsal</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                            {!loading && data !== null && data.map((row, i) => {
                                const wedding = row.wedding;
                                return (
                                    <TableRow key={`row-${i}`}>
                                        <TableCell align="left">{wedding.wedding_date_formatted}</TableCell>
                                        <TableCell align="left" className={classes.coupleCol}>
                                            <IconButton onClick={handleClick(wedding.id)}>
                                                <Icon>person</Icon>
                                            </IconButton>
                                            {wedding.bride_full_name} and {wedding.groom_full_name}
                                        </TableCell>
                                        {showEmails && (
                                            <React.Fragment>
                                                <TableCell align="left">{wedding.bride_email}</TableCell>
                                                <TableCell align="left">{wedding.groom_email}</TableCell>
                                            </React.Fragment>
                                        )}
                                        <TableCell align="left">{wedding.officiant ? wedding.officiant.name : ''}</TableCell>
                                        <TableCell align="left">{wedding.status}</TableCell>
                                        <TableCell align="left">${wedding.balance}</TableCell>
                                        <TableCell align="left">{wedding.invoices}</TableCell>
                                        <TableCell align="left" className={classes.venueCol}>{wedding.venue_name}</TableCell>
                                        <TableCell align="left" className={classes.notesCol}>{wedding.all_notes}</TableCell>
                                        <TableCell align="left">{wedding.location_rehearsal}</TableCell>
                                        <TableCell align="left">{wedding.requires_rehearsal ? 'Yes' : 'No'}</TableCell>
                                    </TableRow>
                                )
                            })}
                            {!loading && data !== null && data.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={showEmails ? 10 : 8} align="center">No data</TableCell>
                                </TableRow>
                            )}
                            {loading && (
                                <TableRow>
                                    <TableCell colSpan={showEmails ? 10 : 8} align="center">
                                        <CircularProgress color="primary" />
                                    </TableCell>
                                </TableRow>
                            )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            </Grid>
        </Paper>
    );
}

export default VenueBalancesReport;
